import { useRequest } from 'features/request/useRequest';
import { graphql } from 'gql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

const getContractGql = graphql(`
    query uerContract($contractId: Long!) {
        uerContract(contractId: $contractId) {
            data {
                costRounding
                hasFilledAddresses
                hasEmptyFormulaInContract
                hasEmptyOtherCostTypeInContract
                hasNoPlanByConstructionType
                account
                documentUrl
                workOrderNavs {
                    actKC2Navs {
                        date
                        id
                        number
                        status
                    }
                    id
                    internalNumber
                    name
                    signingDate
                    status
                    workOrderChanges {
                        id
                        signingDate
                        status
                        workOrderChangeNumber
                        workOrderNumber
                    }
                }
                createdAt
                hasContractChanges
                hasPriceParams
                hasOthers
                isEditablePriceParams
                id
                codeOKUD
                codeOKPD
                customerOKPO
                contractorOKPO
                name
                projectName
                status
                authoredBy
                subject
                internalNumber
                customer
                customerAddress
                contractor
                contractorAddress
                signingDate
                startDate
                finishDate
                amountWithTax
                amountWithoutTax
                tax
                remainder
                note
                supplyBasis
                uerOthersPercent
                constructionTypes {
                    code
                    id
                    name
                    shortName
                    catalogId
                    fullName
                    contractFileName
                    createdAt
                }
                constructionTypeAttributes {
                    constructionType {
                        code
                        id
                        name
                        shortName
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    northernAllowancePercent
                    uerOthersPercent
                }
                hasSupplyBasisInContract
                hasPriceListRatio
                hasFilledMandatoryProperties
                unreadyUerRowCount
                isPriceCollectionLoaded
                otherCosts {
                    id
                    name
                    calculatingType
                    calculatingTypeValue
                    section
                    chapter
                    limits {
                        constructionType {
                            id
                            code
                            name
                            shortName
                            catalogId
                            fullName
                            contractFileName
                            createdAt
                        }
                        season {
                            id
                            year
                        }
                        cost
                    }
                }
                conversionIndices {
                    constructionType {
                        id
                        name
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    id
                    kozp
                    kemm
                    kmtr
                    keqp
                    knr
                    ksp
                    kpir
                }
                seasons {
                    id
                    year
                }
                seasonsInflation {
                    id
                    year
                    inflationRate
                }
                contractPlans {
                    name
                    type
                    contractPlan
                    isHeadLine
                    isEditable
                    constructionTypePlanResults {
                        constructionTypeId
                        contractPlan
                        status
                    }
                }
                cost {
                    contractPlan
                    contractPlanReminderPercent
                    contractPlanReminderWithTax
                    contractPlanTax
                    contractPlanWithTax
                    deviationTip
                    workOrdersPlanCost
                    workOrdersPlanCostWithTax
                }
                statusFollowers
                catalogUerBase {
                    version
                    uniqueId
                    name
                    hasWorkKind
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const getContractConstructionTypesAndSeasonsGql = graphql(`
    query getUerContractConstructionTypesAndSeasons($contractId: Long!) {
        uerContract(contractId: $contractId) {
            data {
                constructionTypes {
                    code
                    id
                    name
                    shortName
                    catalogId
                    fullName
                    contractFileName
                    createdAt
                }
                seasons {
                    id
                    year
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

const getContractResultsGql = graphql(`
    query uerContractResults($contractId: Long!) {
        uerContractResults(contractId: $contractId) {
            result
            message
            data {
                workOrders {
                    id
                    internalNumber
                    signingDate
                    status
                }
                contractResults {
                    type
                    name
                    isHeadLine
                    isEditable
                    contractPlan
                    workOrderPlanSum
                    contractRest
                    deviation
                    deviationTip
                    completedDeviation
                    completedRestDeviationTip
                    completed
                    completedRest
                    workOrderResults {
                        workOrderId
                        planPrice
                        completedPrice
                        deviation
                        constructionTypeResults {
                            constructionTypeId
                            planPrice
                            completedPrice
                        }
                    }
                    constructionTypePlanResults {
                        constructionTypeId
                        contractPlan
                        contractPlanSeasons {
                            contractSeasonId
                            value
                            year
                        }
                        status
                        workOrderPlanSumSeasons {
                            contractSeasonId
                            value
                            year
                        }
                    }
                    code
                    otherCostStatus
                    otherCostTypeUniqueId
                    rowType
                    totalType
                }
            }
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const useContractResultsData = (contractId: number, withEffect = true) => {
    const dispatch = useDispatch();
    const getContractVariables = useMemo(
        () => ({
            contractId,
        }),
        [contractId],
    );

    const [
        getContractResultsData,
        getContractResultsStatus,
        getContractResultsError,
        getContractResultsMakeRequest,
        getContractResultsSetData,
    ] = useRequest({
        requestId: 'getContractResults',
        query: getContractResultsGql,
        withEffect: withEffect && Boolean(contractId),
        variables: getContractVariables,
    });

    const data = useMemo(() => getContractResultsData?.uerContractResults.data, [getContractResultsData]);

    const refreshData = useCallback(() => {
        dispatch(getContractResultsMakeRequest(getContractVariables));
    }, [dispatch, getContractResultsMakeRequest, getContractVariables]);

    return [
        data,
        getContractResultsStatus,
        getContractResultsError,
        getContractResultsMakeRequest,
        getContractResultsSetData,
        refreshData,
    ];
};

export const useContractData = (contractId: number, withEffect = true) => {
    const getContractVariables = useMemo(
        () => ({
            contractId,
        }),
        [contractId],
    );

    const [getContractData, getContractStatus, getContractError, makeContractDataRequest] = useRequest({
        requestId: 'getContract',
        query: getContractGql,
        withEffect: withEffect && Boolean(contractId),
        variables: getContractVariables,
    });

    const data = useMemo(() => getContractData?.uerContract.data, [getContractData]);

    return [data, getContractStatus, getContractError, makeContractDataRequest];
};
